import type {
  Cheque,
  ChequeBulkResult,
  ChequeDirection,
  ChequeListItem,
  ChequeSummary,
  Page,
} from '@sahab/contracts'
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { invalidatePurchasing } from '../purchasing/queries'
import { invalidateSales } from '../sales/queries'
import type { ChequeListParams } from './search'

/**
 * Cheques have their own keys. A step on a cheque also changes its receipt or payment (a bounce or a
 * chargeback voids it), the customer's or supplier's balance and the ledger, so every action refreshes
 * sales, purchasing and the ledger too.
 */
export const chequeKeys = {
  all: ['cheques'] as const,
  list: (query: ChequeListParams) => ['cheques', 'list', query] as const,
  summary: (direction: ChequeDirection, party: ChequeParty = {}) =>
    ['cheques', 'summary', direction, party] as const,
  detail: (id: string) => ['cheques', 'detail', id] as const,
  /** A customer's bounced cheques (the receipt form warns about them). */
  bounced: (customerId: string) => ['cheques', 'bounced', customerId] as const,
}

const path = (id: string, action?: string) =>
  `/cheques/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

export function chequeListQueryOptions(query: ChequeListParams) {
  return queryOptions({
    queryKey: chequeKeys.list(query),
    queryFn: ({ signal }) => api.get<Page<ChequeListItem>>('/cheques', { signal, query: { ...query } }),
    placeholderData: keepPreviousData,
  })
}

/** A party's figures: a customer's received cheques or a supplier's issued ones. */
export interface ChequeParty {
  customerId?: string
  supplierId?: string
}

/** The register's figures, or one party's (its profile's Cheques tab). */
export function chequeSummaryQueryOptions(direction: ChequeDirection, party: ChequeParty = {}) {
  return queryOptions({
    queryKey: chequeKeys.summary(direction, party),
    queryFn: ({ signal }) =>
      api.get<ChequeSummary>('/cheques/summary', { signal, query: { direction, ...party } }),
  })
}

export function chequeQueryOptions(id: string) {
  return queryOptions({
    queryKey: chequeKeys.detail(id),
    queryFn: ({ signal }) => api.get<Cheque>(path(id), { signal }),
  })
}

/** How many cheques of this customer bounced (all time): the newest one, for the warning's link. */
export function bouncedChequesQueryOptions(customerId: string) {
  return queryOptions({
    queryKey: chequeKeys.bounced(customerId),
    queryFn: ({ signal }) =>
      api.get<Page<ChequeListItem>>('/cheques', {
        signal,
        query: { direction: 'RECEIVED', customerId, status: 'BOUNCED', sort: '-chequeDate', pageSize: 1 },
      }),
    staleTime: 60_000,
  })
}

function useChequesChanged() {
  const queryClient = useQueryClient()
  return (cheques: readonly { id: string }[] | Cheque) =>
    Promise.all([
      'events' in cheques ? queryClient.setQueryData(chequeKeys.detail(cheques.id), cheques) : undefined,
      queryClient.invalidateQueries({ queryKey: chequeKeys.all }),
      invalidateSales(queryClient, { ledger: true }),
      invalidatePurchasing(queryClient, { ledger: true }),
    ])
}

export type ChequeStep =
  | { action: 'deposit'; date: string; treasuryAccountId: string }
  | { action: 'clear'; date: string }
  | { action: 'bounce' | 'return' | 'cancel' | 'chargeback'; date: string; reason: string }
  | { action: 'undo'; reason: string }

/** One step on one cheque (its version guards against a step someone else took meanwhile). */
export function useChequeStep(cheque: { id: string; version: number }) {
  const changed = useChequesChanged()
  return useMutation({
    mutationFn: ({ action, ...body }: ChequeStep) =>
      api.post<Cheque>(path(cheque.id, action), { ...body, version: cheque.version }),
    onSuccess: (next) => changed(next),
  })
}

export type ChequeBulkStep =
  | { action: 'deposit'; chequeIds: string[]; date: string; treasuryAccountId: string }
  | { action: 'clear'; chequeIds: string[]; date: string }

/** Many cheques on one deposit slip, or cleared on one bank statement day: all or none. */
export function useChequeBulkStep() {
  const changed = useChequesChanged()
  return useMutation({
    mutationFn: ({ action, ...body }: ChequeBulkStep) =>
      api.post<ChequeBulkResult>(`/cheques/${action}`, body),
    onSuccess: (result) => changed(result.items),
  })
}

export function useChequeNotes(cheque: { id: string; version: number }) {
  const changed = useChequesChanged()
  return useMutation({
    mutationFn: (notes: string) =>
      api.post<Cheque>(path(cheque.id, 'notes'), { notes, version: cheque.version }),
    onSuccess: (next) => changed(next),
  })
}
